import React, { useEffect, useState } from "react";
import { useHistory, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { client } from "@passwordless-id/webauthn";
import Cookies from "js-cookie";
import platform from "platform";

import NonAuthLayout from "../../components/header/NonAuthHeaderLayout";
import { ReactComponent as FaceID } from "../../../../assets/img/faceid.svg";
import { ReactComponent as BackArrow } from "../../../../assets/img/backArrow.svg";
import { ReactComponent as IgreeQuestionIcon } from "../../../../assets/img/fingerprint.svg";
import { ReactComponent as ShieldIcon } from "../../../../assets/img/shield.svg";
import { ReactComponent as PinIcon } from "../../../../assets/img/pin-c.svg";
import { Portal } from "../../../AuthenticatedApp/components/modals/Portal";
import SuccessModal from "../../../AuthenticatedApp/components/modals/SuccessModal";
import PassKeyFailedModal from "./PassKeyFailedModal";
import { postCall } from "../../../../networking";
import { urls } from "../../../../networking/url";
import { decodeBase64 } from "../../../../utils/utilityFunctions";
import { REASON } from "../../../../eventLogger/segment/constants/eventProperties";
import {
    PASSKEY_SETUP_COMPLETE,
    PASSKEY_SETUP_FAILED,
} from "../../../../eventLogger/segment/constants/eventTags";
import * as eventLogger from "../../../../eventLogger";

const SUCCESS_MODAL = "SUCCESS_MODAL";
const ERROR = "ERROR";

const CreateAPasskey = () => {
    const history = useHistory();
    const { smeUserID } = useSelector((state) => state.signInState);

    const email = sessionStorage.getItem("kudaUserEmail");
    const userIDInLocalStorage = sessionStorage.getItem("kudaUserID");
    const completelyOnboarded = sessionStorage.getItem("completelyOnboarded");
    const userID = smeUserID ? smeUserID : userIDInLocalStorage;

    const [modal, setModal] = useState("");
    const [fetchingPasskey, setFetchingPasskey] = useState(false);
    const [userTriedPasskey, setUserTriedPasskey] = useState(false);

    const resetModal = () => setModal("");

    const handleError = () => {
        setFetchingPasskey(false);
        setUserTriedPasskey(true);
        setModal(ERROR);
    };

    const handleSuccessModalClose = () => {
        if (completelyOnboarded) {
            history.push({
                pathname: "/select-profile",
                state: { redirectPath: history.location?.state?.redirectPath },
            });
            sessionStorage.removeItem("completelyOnboarded");
        } else {
            const token = sessionStorage.getItem("kudaAppToken");

            Cookies.set("kudaAppToken", token, {
                secure: true,
                path: "/",
            });

            history.push("/home");
        }
    };

    const createPasskey = async () => {
        setFetchingPasskey(true);

        try {
            const response = await postCall(
                urls.createPasskeyOptions,
                { email },
                {},
                { clientType: "web" },
            );
            const options = response.data.data.options;

            const optionsObj = {
                ...options,
                domain: options.rp.id,
                user: {
                    ...options.user,
                    id: decodeBase64(options.user.id),
                },
                discoverable: options.requireResidentKey,
            };

            const attestationResponse = await client.register(optionsObj);

            const createPasskeyPayload = {
                attestationResponse,
                device: {
                    deviceId: attestationResponse.id,
                    deviceName: `${platform.name} ${platform.os.family}`,
                    deviceType: 3, // 3 is ENUM for Web
                },
                sessionId: response.data.data.sessionId,
            };
            const createPasskeyResponse = await postCall(
                urls.createPasskey,
                createPasskeyPayload,
                {},
                { clientType: "web" },
            );
            setFetchingPasskey(false);

            if (createPasskeyResponse?.data.isSuccessful) {
                setModal(SUCCESS_MODAL);

                eventLogger.pushEventGlobally(PASSKEY_SETUP_COMPLETE);
            } else {
                handleError();
                const segmentData = {
                    [REASON]: createPasskeyResponse?.data?.message,
                };
                eventLogger.pushEventGlobally(PASSKEY_SETUP_FAILED, segmentData);
            }
        } catch (error) {
            handleError();
        }
    };

    useEffect(() => {
        if (!userID) {
            history.push("/login");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [smeUserID, userIDInLocalStorage]);

    return (
        <React.Fragment>
            <NonAuthLayout type="authenticate" />

            <div className="flex login-container--wrap">
                <Container className="animated kudaFadeInUp">
                    <h4 className="page-heading-title color-primary text-center text-xlbold animated kudaFadeInUp bottom-spacer--md">
                        Create A Passkey
                    </h4>

                    <Card>
                        <Link to="/" className="back-to color-primary">
                            <span className="backArrow">
                                <BackArrow />
                            </span>
                            Back
                        </Link>

                        <FaceID />

                        <div className="w-100">
                            <div className="btn bill-item width-100">
                                <div className="bill-item-wrap text-left">
                                    <span className="bill-item--icon">
                                        <IgreeQuestionIcon />
                                    </span>
                                    <div>
                                        <p className="color-black text-xlbold bill-label">
                                            No Password or Passcode Needed
                                        </p>
                                        <p className="color-faded text-mxs">
                                            Tell us what you want to use Kuda for.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="btn bill-item width-100">
                                <div className="bill-item-wrap text-left">
                                    <span className="bill-item--icon">
                                        <ShieldIcon />
                                    </span>
                                    <div>
                                        <p className="color-black text-xlbold bill-label">
                                            Increased Security
                                        </p>
                                        <p className="color-faded text-mxs">
                                            Add phishing-resistant technology to secure your
                                            account.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="btn bill-item width-100">
                                <div className="bill-item-wrap text-left">
                                    <span className="bill-item--icon">
                                        <PinIcon />
                                    </span>
                                    <div>
                                        <p className="color-black text-xlbold bill-label">
                                            Sign In Privately
                                        </p>
                                        <p className="color-faded text-mxs">
                                            Your Face ID, Touch ID, Biometrics or Passcode data
                                            stays on your device.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-column align-center justify-center btn-group">
                            <button
                                type="button"
                                className={`kuda-cta cursor-pointer mb-3 ${
                                    fetchingPasskey ? "form-loading disable-dead" : ""
                                }`}
                                disabled={fetchingPasskey}
                                onClick={createPasskey}
                            >
                                <span>Create A Passkey</span>
                            </button>

                            {userTriedPasskey && (
                                <button
                                    type="button"
                                    className="cursor-pointer isTrue text-bold bg-white"
                                    onClick={handleSuccessModalClose}
                                >
                                    <span>Maybe Later</span>
                                </button>
                            )}
                        </div>
                    </Card>
                </Container>
            </div>

            {modal === SUCCESS_MODAL && (
                <Portal>
                    <SuccessModal
                        message="You’ve Created A Passkey"
                        doSomethingElse={handleSuccessModalClose}
                    >
                        <div className="password-set--message">
                            <p className="text-center">
                                You can now sign in to your account on this device without a
                                password.
                            </p>
                        </div>
                    </SuccessModal>
                </Portal>
            )}

            {modal === ERROR && (
                <Portal>
                    <PassKeyFailedModal {...{ onClose: resetModal }} />
                </Portal>
            )}
        </React.Fragment>
    );
};

const Container = styled.div`
    width: 100%;
    max-width: 600px;
    margin-right: auto;
    margin-left: auto;
    position: relative;
    padding: 160px 20px 40px;
    display: flex;
    flex-direction: column;
    align-items: center;

    @media screen and (min-width: 375px) {
        padding-top: 134px;
    }

    .btn-group {
        column-gap: 20px;

        .kuda-cta {
            width: 176px;
        }
    }

    .bill-item {
        box-shadow: none;
        border: none;
        padding: 12.5px 24px;
        height: unset;
        cursor: default;

        * {
            pointer-events: unset;
        }

        &--icon {
            width: 32px;
            height: 36px;
            box-shadow: 0px 0px 10px 0px #0000000f;
            svg {
                height: unset;
            }
        }

        &:not(:last-child) {
            margin-bottom: 8px;
        }

        .color-faded {
            width: 100%;
            max-width: 295px;
        }
    }

    .bill-label {
        margin-bottom: 2px;
    }
`;

const Card = styled.div`
    position: relative;
    box-shadow: 15px 30px 40px 0px #00000012;
    border: 1px solid #dbdce080;
    border-radius: 6px;
    padding: 20px;
    width: 100%;
    background-color: #ffffff;
    display: flex;
    flex-direction: column;
    row-gap: 40px;
    align-items: center;

    @media screen and (min-width: 768px) {
        padding: 48px 69px 64px;
    }
`;

export default CreateAPasskey;
